'use client'

import clsx from 'clsx'
import React, { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useClickOutside } from './hooks/useClickOutside'
import { usePopoverPosition } from './hooks/usePopoverPosition'

import styles from './Popover.module.scss'

export type PopoverPlacement =
	| 'top'
	| 'topLeft'
	| 'topRight'
	| 'bottom'
	| 'bottomLeft'
	| 'bottomRight'
	| 'left'
	| 'leftTop'
	| 'leftBottom'
	| 'right'
	| 'rightTop'
	| 'rightBottom'

export type PopoverTrigger = 'hover' | 'click' | 'focus' | 'contextMenu'

export interface PopoverProps {
	/** Содержимое popover */
	content: ReactNode
	/** Заголовок popover */
	title?: ReactNode
	/** Триггер для показа popover */
	trigger?: PopoverTrigger | PopoverTrigger[]
	/** Расположение popover относительно элемента */
	placement?: PopoverPlacement
	/** Контролируемое состояние видимости */
	open?: boolean
	/** Callback при изменении видимости */
	onOpenChange?: (open: boolean) => void
	/** Показывать ли стрелку */
	arrow?: boolean
	/** CSS класс для overlay */
	overlayClassName?: string
	/** Inline стили для overlay */
	overlayInnerStyle?: CSSProperties
	/** CSS класс для wrapper */
	className?: string
	/** Inline стили для wrapper */
	style?: CSSProperties
	/** Тег обёртки триггера: span - для inline-текста */
	as?: 'div' | 'span'
	/** Дочерние элементы (триггер) */
	children: ReactNode
	/** Z-index для popover */
	zIndex?: number
	/** Задержка перед показом (мс) для hover */
	mouseEnterDelay?: number
	/** Задержка перед скрытием (мс) для hover */
	mouseLeaveDelay?: number
}

/**
 * Popover component - независимая реализация всплывающего окна
 *
 * @example
 * ```tsx
 * <Popover
 *   content="Popover content"
 *   title="Popover title"
 *   trigger="hover"
 *   placement="top"
 * >
 *   <Button>Hover me</Button>
 * </Popover>
 * ```
 */
export const Popover: React.FC<PopoverProps> = ({
	content,
	title,
	trigger = 'hover',
	placement = 'top',
	open: controlledOpen,
	onOpenChange,
	arrow = true,
	overlayClassName,
	overlayInnerStyle,
	className,
	style,
	as = 'div',
	children,
	zIndex = 1050,
	mouseEnterDelay = 100,
	mouseLeaveDelay = 100
}) => {
	const [internalOpen, setInternalOpen] = useState(false)
	const isControlled = controlledOpen !== undefined
	const open = isControlled ? controlledOpen : internalOpen

	const triggerRef = useRef<HTMLElement>(null)
	const popoverRef = useRef<HTMLDivElement>(null)
	const enterTimerRef = useRef<NodeJS.Timeout>()
	const leaveTimerRef = useRef<NodeJS.Timeout>()

	const triggers = Array.isArray(trigger) ? trigger : [trigger]

	const setOpen = useCallback(
		(newOpen: boolean) => {
			if (!isControlled) {
				setInternalOpen(newOpen)
			}
			onOpenChange?.(newOpen)
		},
		[isControlled, onOpenChange]
	)

	const handleOpen = useCallback(() => {
		clearTimeout(leaveTimerRef.current)
		if (mouseEnterDelay > 0) {
			enterTimerRef.current = setTimeout(() => setOpen(true), mouseEnterDelay)
		} else {
			setOpen(true)
		}
	}, [mouseEnterDelay, setOpen])

	const handleClose = useCallback(() => {
		clearTimeout(enterTimerRef.current)
		if (mouseLeaveDelay > 0) {
			leaveTimerRef.current = setTimeout(() => setOpen(false), mouseLeaveDelay)
		} else {
			setOpen(false)
		}
	}, [mouseLeaveDelay, setOpen])

	const handleToggle = useCallback(() => {
		setOpen(!open)
	}, [open, setOpen])

	// Обработчики для триггеров
	const triggerProps: any = {}

	if (triggers.includes('hover')) {
		triggerProps.onMouseEnter = handleOpen
		triggerProps.onMouseLeave = handleClose
	}

	if (triggers.includes('click')) {
		triggerProps.onClick = handleToggle
	}

	if (triggers.includes('focus')) {
		triggerProps.onFocus = handleOpen
		triggerProps.onBlur = handleClose
	}

	if (triggers.includes('contextMenu')) {
		triggerProps.onContextMenu = (e: React.MouseEvent) => {
			e.preventDefault()
			handleToggle()
		}
	}

	// Закрытие при клике вне компонента (только для click trigger)
	useClickOutside([triggerRef, popoverRef], () => {
		if (triggers.includes('click') && open) {
			setOpen(false)
		}
	})

	// Вычисление позиции popover
	const position = usePopoverPosition({
		triggerRef,
		popoverRef,
		placement,
		open,
		arrow
	})

	// Очистка таймеров
	useEffect(() => {
		return () => {
			clearTimeout(enterTimerRef.current)
			clearTimeout(leaveTimerRef.current)
		}
	}, [])

	// Portal для popover
	const portalTarget = typeof document !== 'undefined' ? document.body : null
	const triggerClassName = clsx(className, as === 'span' && styles.triggerInline)

	const overlay =
		open &&
		portalTarget &&
		createPortal(
			<div
				ref={popoverRef}
				className={`${styles.popover} ${overlayClassName || ''}`}
				style={{
					...position,
					zIndex,
					...overlayInnerStyle
				}}
				onMouseEnter={triggers.includes('hover') ? handleOpen : undefined}
				onMouseLeave={triggers.includes('hover') ? handleClose : undefined}>
				<div className={styles.popover__content}>
					{title && <div className={styles.popover__title}>{title}</div>}
					<div className={styles.popover__inner}>{content}</div>
				</div>
				{arrow && <div className={styles.popover__arrow} data-placement={placement} />}
			</div>,
			portalTarget
		)

	if (as === 'span') {
		return (
			<>
				<span ref={triggerRef} className={triggerClassName} style={style} {...triggerProps}>
					{children}
				</span>
				{overlay}
			</>
		)
	}

	return (
		<>
			<div ref={triggerRef} className={triggerClassName} style={style} {...triggerProps}>
				{children}
			</div>
			{overlay}
		</>
	)
}
